import React, { useEffect, useState, useCallback } from "react";
import "@sia/style-guide/dist/base.css";
import { Route, Switch, useHistory, Redirect } from "react-router-dom";
import { init as initI18n, getBrowserLanguageKey } from "common/Translation/i18n";
import IDPAdapter from "common/Adapters/IDPAdapter/IDPAdapter";
import NotFoundPage from "./pages/Error/NotFoundPage";
import GlobalContext from "contexts/GlobalContext";
import { useTranslation } from "react-i18next";
import { useTranslator } from "./common/Translation/Translator";
import httpClient from "common/HttpClient";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import User from "./common/User";
import { usePathFactory } from "./common/Path/PathFactoryHook";
import memberTypeRepository from "common/Adapters/WebPlatformAdapter/MemberTypes/MemberTypeRepository";
import { Body, Footer, Layout, Loader, usePrivacyPolicy } from "@sia/style-guide";
import { useFooterContents } from "./common/Layout/FooterContents";
import isGoogleBot from "./common/GoogleBots";
import { Helmet, HelmetProvider } from "react-helmet-async";
import ErrorPage from "./pages/Error/ErrorPage";
import HttpErrorContextProvider from "contexts/HttpErrorContext";
import CMSContent from "pages/CMSContent/CMSContent";
import cmsAdapter from "./common/Adapters/CMSAdapter/CMSAdapter";
import CMSSearch from "pages/CMSContent/CMSSearch";
import WizardProvider from "contexts/WizardContext";
import CookieClient from "./components/CookieClient";
import UnsubscribeFromNewsletter from "pages/Newsletter/UnsubscribePage";
import HeaderClient from "components/HeaderClient";
import Catalog from "pages/Catalog/Catalog";
import Agenda from "pages/Agenda/Agenda";
import AgendaAppPage from "pages/Agenda/AgendaAppPage";
import { getLocale } from "common/Translation/i18n";

initI18n();

function App() {
  const { i18n } = useTranslation();
  const translate = useTranslator();
  const history = useHistory();
  const pathFactory = usePathFactory();
  const [isLoaderShown, setIsLoaderShown] = useState(false);
  httpClient.setLoaderStateCallback(setIsLoaderShown);
  const [menuItems, setMenuItems] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [noMenuPages, setNoMenuPages] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [isLoadingPages, setIsLoadingPages] = useState(process.env.REACT_APP_CMS_URL ? true : false);
  const [organizationLanguages, setOrganizationLanguages] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [organisationData, setOrganisationData] = useState({});
  const [user, setUser] = useState(null);
  const [headerUser, setHeaderUser] = useState(null);
  const [isIDPInitialized, setIsIDPInitialized] = useState(false);
  const [wasIDPInitializationStarted, setWasIDPInitializationStarted] = useState(false);
  const [language, setLanguage] = useState(null);
  const footerContents = useFooterContents(language);
  const [isSubscribedToAnyService, setIsSubscribedToAnyService] = useState(false);
  const organizationCode = window.location.hostname.split(".")[0];
  const localStorageLanguageKey = localStorage.getItem("sia.webPlatform.languageKey");
  const globalContextValue = {
    isLoaderShown,
    setIsLoaderShown,
    isIDPInitialized: isIDPInitialized,
    user: user,
    language,
    isSubscribedToAnyService,
    menuItems,
    noMenuPages,
    organizationCode,
    organisationData,
  };

  const { PrivacyPolicyComponent, handlePrivacyPolicyNotification } = usePrivacyPolicy({
    webPlatformAdapter,
    getLocale,
    translate,
    pathFactory,
    linkUrl: `${process.env.REACT_APP_WEBSITE_URL}${pathFactory.create("privacy-policy")}`,
  });

  const defaultAppSwitcherValue = isGoogleBot(navigator.userAgent);
  const getUrlLanguageKey = useCallback(() => {
    const pathSplit = history.location.pathname.split("/");
    return pathSplit[1];
  }, [history.location.pathname]);

  const isValidLanguageKey = useCallback((languageKey) => organizationLanguages.includes(languageKey), [organizationLanguages]);

  const getApplyingLanguage = useCallback(() => {
    const urlLanguageKey = getUrlLanguageKey();
    const validUrlLanguageKey = isValidLanguageKey(urlLanguageKey) ? urlLanguageKey : null;
    if (validUrlLanguageKey) return validUrlLanguageKey;
    if (localStorageLanguageKey && isValidLanguageKey(localStorageLanguageKey)) {
      return localStorageLanguageKey;
    }
    const browserLang = getBrowserLanguageKey();
    return isValidLanguageKey(browserLang) ? browserLang : organizationLanguages[0];
  }, [localStorageLanguageKey, getUrlLanguageKey, isValidLanguageKey, organizationLanguages]);

  useEffect(() => {
    const urlLanguageKey = getUrlLanguageKey();
    if (organizationLanguages.length > 0 && urlLanguageKey && !isValidLanguageKey(urlLanguageKey)) {
      const validLanguage = organizationLanguages[0];
      const newUrl = createLanguageKeyReplacedUrl(validLanguage);
      i18n.changeLanguage(validLanguage, () => {
        setLanguage(validLanguage);
        window.history.replaceState(null, "", newUrl);
      });
    }
  }, [organizationLanguages, getUrlLanguageKey, isValidLanguageKey, i18n, setLanguage, language]);

  const createHeaderUser = (user, customerData, originalMemberType) => {
    const id = parseFloat(user.getSiaId());
    if (user.isMemberTypeGroupOf("single")) {
      return {
        id: id,
        name: [customerData.firstname, customerData.lastname].join(" "),
        type: originalMemberType,
      };
    }
    if (user.isMemberTypeGroupOf("corporate")) {
      return {
        id: id,
        name: customerData.name,
        additionalName: customerData.additional_name,
        type: originalMemberType,
      };
    }
  };

  useEffect(() => {
    if (wasIDPInitializationStarted || organizationLanguages.length === 0) {
      return;
    }
    i18n.changeLanguage(getApplyingLanguage(), () => {
      const lang = isValidLanguageKey(i18n.language) ? i18n.language : organizationLanguages[0];
      setLanguage(lang);
      process.env.REACT_APP_CMS_URL && loadAliases(lang);
    });
    setIsLoaderShown(true); // todo, refactor loader handling SW-167
    setWasIDPInitializationStarted(true);
    IDPAdapter.init((idpUser) => {
      setIsIDPInitialized(true);
      setIsLoaderShown(false);
      if (idpUser) {
        if (localStorage.getItem("sia.webPlatform.forceUserLanguage")) {
          localStorage.removeItem("sia.webPlatform.forceUserLanguage");
          localStorage.setItem("sia.webPlatform.languageKey", idpUser.getLocale());
          const languageKeyReplacedUrl = createLanguageKeyReplacedUrl(idpUser.getLocale());
          window.location.replace(languageKeyReplacedUrl);
          return;
        }
        const servicesRoles = ["sia_role_member_corporate", "sia_role_member_single"];
        if (servicesRoles.includes(idpUser.role)) {
          setIsSubscribedToAnyService(true);
        }
        webPlatformAdapter.getLoginInformation(i18n.language, (response) => {
          const originalMemberType = response.data[0].member_type.name;
          const memberType = memberTypeRepository.getById(response.data[0].member_type.id);
          const hasSubsidiaries = response.data[0].has_subsidiaries;
          const user = new User(idpUser.getUsername(), memberType, idpUser.getLocale(), idpUser.getRole(), originalMemberType, hasSubsidiaries);
          webPlatformAdapter.getCustomerData(user, (response) => {
            const customerData = response.data[0];
            setHeaderUser(createHeaderUser(user, customerData, originalMemberType));
            setUser(user);
          });
        });
        handlePrivacyPolicyNotification();
      }
    });
    // eslint-disable-next-line
  }, [wasIDPInitializationStarted, i18n, getApplyingLanguage, translate, organizationLanguages]);

  const createLanguageKeyReplacedUrl = (languageKey) => {
    let pathParts = window.location.pathname.split("/");
    pathParts[1] = languageKey;
    const hash = window.location.hash ? window.location.hash : "";
    const search = window.location.search ? window.location.search : "";
    return window.location.origin + pathParts.join("/") + search + hash;
  };

  const renderPage = (component) => {
    const urlLanguageKey = getUrlLanguageKey();
    if (urlLanguageKey && !isValidLanguageKey(urlLanguageKey)) {
      return <NotFoundPage />;
    }
    return component;
  };

  useEffect(() => {
    if (language && organizationCode) {
      loadMenu(organizationCode);
    }
    // eslint-disable-next-line
  }, [language, organizationCode]);

  useEffect(() => {
    if (organizationCode && organizationLanguages?.length === 0) {
      fetchOrganizationData(organizationCode);
    }
    // eslint-disable-next-line
  }, [organizationCode, history.location]);

  const loadMenu = async (organizationCode) => {
    await cmsAdapter.fetchMenuItemsByOrganization(
      language,
      organizationCode,
      ({ menuItems }) => {
        setMenuItems(menuItems);
        loadNoMenu();
      },
      history
    );
  };
  async function fetchOrganizationData(organizationCode) {
    try {
      const l = history.location.pathname.split("/")[1] || "de";
      await cmsAdapter.fetchOrganizationData(organizationCode, l, (data) => {
        const languages = data?.language.map((lang) => lang.id);
        if (languages && languages.length > 0) {
          setOrganizationLanguages(languages);
        } else {
          setOrganizationLanguages(["de", "fr", "it", "en"]);
        }
        setOrganisationData(data);
      });
    } catch (error) {
      setOrganizationLanguages(["de", "fr", "it", "en"]);
    }
  }

  async function loadNoMenu() {
    await cmsAdapter.fetchOrgaNoMenu(
      language,
      ({ data }) => {
        setNoMenuPages(data);
        setIsLoadingPages(false);
      },
      organizationCode
    );
  }

  async function loadAliases(lang) {
    const alias = history.location.pathname;
    await cmsAdapter.fetchAliases(lang, alias, (data) => {
      const findAlias = data?.length > 0 && data[0];
      if (findAlias?.alias) {
        const usedLang = findAlias.lang || lang;
        if (usedLang !== lang) localStorage.setItem("sia.webPlatform.languageKey", usedLang);
        if (findAlias.destination) {
          window.location.href = findAlias.destination.startsWith("http") ? findAlias.destination : `/${usedLang}${findAlias.destination}`;
        } else {
          const to = `/${usedLang}/cms/node/${findAlias.node}${findAlias.section_element ? `#${findAlias.section_element}` : ""}`;
          window.location.replace(to);
        }
      }
    });
  }

  const contactPage = organisationData?.contact_page || null;
  const newsletterPage = organisationData?.newsletter_page || null;
  const footerData = organisationData?.footer_data || {};

  const footerLinks = [
    ...(newsletterPage
      ? [
          {
            target: "/de/cms/node/" + newsletterPage,
            label: "layout.footer.links.newsletter",
          },
        ]
      : []),
    {
      target: "privacy-policy",
      label: "layout.footer.links.privacy-policy.orga",
    },
  ];

  const contactUsTarget = contactPage ? `/${language}/cms/node/` + contactPage : `https://www.sia.ch/${language}/cms/node/52`;
  const contactUs = {
    title: "layout.footer.contact.contact-us",
    buttons: [
      {
        target: contactUsTarget,
        label: "layout.footer.contact.contact",
      },
    ],
  };
  const socialNetworks = organisationData.social_networks
    ? Object.fromEntries(organisationData.social_networks?.map((el) => [el.name?.toLowerCase(), el.url]))
    : {};
  const follow = {
    newsletterButton: footerContents.follow.newsletterButton,
    title: footerContents.follow.title,
    social: {
      ...socialNetworks,
    },
  };
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <html lang={i18n.language} />
        </Helmet>
      </HelmetProvider>
      {isLoaderShown && <Loader />}
      <Layout>
        <HeaderClient
          pathFactory={pathFactory}
          history={history}
          user={user}
          menuItems={menuItems}
          i18n={i18n}
          createLanguageKeyReplacedUrl={createLanguageKeyReplacedUrl}
          translate={translate}
          headerUser={headerUser}
          organizationCode={organizationCode}
          defaultAppSwitcherValue={defaultAppSwitcherValue}
          availableLanguages={organizationLanguages}
          language={language}
          organisationId={organisationData?.footer_data?.id}
        />
        <Body>
          <GlobalContext.Provider value={globalContextValue}>
            <HttpErrorContextProvider>
              <WizardProvider>
                <Switch>
                  {organizationLanguages.length && <Route exact path="/" component={() => <Redirect to={`/${getApplyingLanguage()}`} />} />}
                  {organizationLanguages.length && (
                    <Route exact path="/:languageKey" render={() => renderPage(<CMSContent nodeId={null} isOrgaHomePage />)} />
                  )}
                  {noMenuPages?.map((page) => {
                    return <Route key={page.id} path={`/:languageKey/cms${page.alias}`} render={() => renderPage(<CMSContent nodeId={page.id} />)} />;
                  })}
                  {!isLoadingPages && <Route path="/:languageKey/cms/hash/:alias" render={() => renderPage(<CMSContent isHash />)} />}
                  {!isLoadingPages && <Route path="/:languageKey/cms/:alias" render={() => renderPage(<CMSContent />)} />}
                  {!isLoadingPages && <Route path="/:languageKey/search/:text" render={() => renderPage(<CMSSearch />)} />}
                  <Route path="/:languageKey/catalog" render={() => renderPage(<Catalog />)} />
                  <Route exact path="/:languageKey/agenda" render={() => renderPage(<Agenda />)} />
                  <Route exact path="/:languageKey/agenda/:id" render={() => renderPage(<AgendaAppPage isEventOccurrence={true} />)} />
                  <Route exact path="/:languageKey/agenda/speaker/:id" render={() => renderPage(<AgendaAppPage />)} />
                  <Route
                    path="/:languageKey/newsletter/unsubscribe/:hashed_key/:product_id"
                    render={() => renderPage(<UnsubscribeFromNewsletter />)}
                  />
                  <Route path="/:languageKey/error" component={ErrorPage} />
                  <Route path="*" component={isLoadingPages ? () => <></> : NotFoundPage} />
                </Switch>
              </WizardProvider>
            </HttpErrorContextProvider>
          </GlobalContext.Provider>
          <CookieClient history={history} lang={language} />
        </Body>
        <Footer
          follow={follow}
          contactUs={contactUs}
          links={footerLinks}
          about={null}
          translate={translate}
          customAbout={
            <p>
              {footerData &&
                Array.from({ length: 9 }, (_, i) => footerData[`address_line_${i + 1}`])
                  .filter((el) => el)
                  .map((line, index) => (
                    <span key={index}>
                      {index > 0 && <br />}
                      {line}
                    </span>
                  ))}
            </p>
          }
        />
      </Layout>
      <PrivacyPolicyComponent />
    </>
  );
}

export default App;
